import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

import { ANALYTICS_QUERY_KEY } from "./useSummary";
import {
  DELETED_IMPORT_JOBS_QUERY_KEY,
  IMPORT_JOBS_QUERY_KEY,
} from "./useImportJobs";
import { TRADES_QUERY_KEY } from "./useTrades";

export type RestoreBatchResult = components["schemas"]["RestoreBatchResultResponse"];

export type RestoreImportBatchVariables = {
  jobId: string;
};

/**
 * Restore a soft-deleted import batch and its trades (the undo path, shared by
 * the trash "Restore" button and the post-delete Undo toast). Invalidates the
 * same surfaces as the delete so the restored trades reappear everywhere.
 */
export function useRestoreImportBatch() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<RestoreBatchResult, Error, RestoreImportBatchVariables>({
    mutationFn: async ({ jobId }) => {
      const { data, error } = await client.POST("/api/imports/jobs/{job_id}/restore", {
        params: { path: { job_id: jobId } },
      });
      if (error !== undefined) {
        const detail =
          typeof error === "object" && error !== null && "detail" in error
            ? String((error as { detail: unknown }).detail)
            : "Failed to restore import.";
        throw new Error(detail);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: IMPORT_JOBS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DELETED_IMPORT_JOBS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: TRADES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY });
    },
  });
}
